/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICrossEntityInventoryNode, ICrossEntityStockLabel } from './cross-entity-inventory-types';
import { InventoryLevels } from './product-inventory-information';

/**
 * Configuration consumed by the stock-label derivation. Thresholds normally flow from
 * `app.config.outOfStockThreshold` and `app.config.lowStockThreshold`.
 */
export interface ICrossEntityStockLabelConfig {
    enableStockCheck?: boolean;
    outOfStockThreshold?: number;
    lowStockThreshold?: number;
    inventoryLevel?: InventoryLevels;
    inventoryRanges?: string;
}

/**
 * Gets the Cross-LE quantity represented by the configured inventory level.
 * @param node - The product-scoped node.
 * @param inventoryLevel - The Site Builder inventory-level setting.
 * @returns Total or physical available quantity.
 */
export const getCrossEntityInventoryQuantity = (node: ICrossEntityInventoryNode | undefined, inventoryLevel?: InventoryLevels): number => {
    const totalAvailable = node?.availableQuantity ?? 0;
    return inventoryLevel === InventoryLevels.physicalAvailable ? Math.max(0, totalAvailable - (node?.orderedSum ?? 0)) : totalAvailable;
};

/**
 * Derives an In/Low/Out-of-stock label for a product-scoped node using two configurable thresholds.
 *
 * Rules (apply to both B2B and B2C):
 * - stock check disabled                  => no label.
 * - qty &lt;= outOfStockThreshold         => OUT OF STOCK (no number).
 * - qty &lt;= lowStockThreshold           => LOW STOCK (number shown).
 * - otherwise                             => IN STOCK (no number).
 *
 * Out-of-stock is always evaluated first so a misconfigured `lowStockThreshold &lt; outOfStockThreshold`
 * never hides an out-of-stock node.
 * @param node - The product-scoped node.
 * @param config - The threshold configuration.
 * @returns The derived stock label, or undefined when stock check is disabled.
 */
export const deriveCrossEntityStockLabel = (
    node: ICrossEntityInventoryNode | undefined,
    config: ICrossEntityStockLabelConfig
): ICrossEntityStockLabel | undefined => {
    if (!config.enableStockCheck) {
        return undefined;
    }

    const quantity = getCrossEntityInventoryQuantity(node, config.inventoryLevel);
    const outOfStockThreshold = config.outOfStockThreshold ?? 0;
    const lowStockThreshold = config.lowStockThreshold;

    if (quantity <= outOfStockThreshold) {
        return { status: 'outOfStock' };
    }

    if (lowStockThreshold !== undefined && quantity <= lowStockThreshold) {
        return { status: 'lowStock', quantity };
    }

    return { status: 'inStock' };
};

/**
 * Determines whether a derived Cross-LE label is enabled by the Site Builder inventory-range setting.
 * @param stockLabel - The derived stock label.
 * @param inventoryRanges - The Site Builder inventory-range setting.
 * @returns Whether the label should be displayed.
 */
export const isCrossEntityStockLabelVisible = (stockLabel: ICrossEntityStockLabel | undefined, inventoryRanges?: string): boolean => {
    if (!stockLabel) {
        return false;
    }

    switch (inventoryRanges) {
        case 'lowAndOutofStock':
            return stockLabel.status !== 'inStock';
        case 'outOfStock':
            return stockLabel.status === 'outOfStock';
        case 'all':
            return true;
        default:
            return false;
    }
};
